// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Hook de Alertas Internos
// Calcula alertas em tempo real a partir dos dados do sistema
// ═══════════════════════════════════════════════════════════════════
import { useMemo } from "react";
import { addDiasUteis, validarNFCache } from "./negocio";
import { vigStatus } from "./formatters";
import { ALERTAS_SSW, contarAlertasSsw } from "./rastreioSsw";
import { hojeSP, inicioJanela } from "./janelaNotas";

function brToISO(br) {
  if (!br) return "";
  const p = br.split("/");
  return p.length === 3 ? p[2]+"-"+p[1]+"-"+p[0] : "";
}

/**
 * Tabelas expiradas de transportadora que ficou SEM tabela vigente hoje.
 * Transportadora com tabela vigente e uma velha vencida não é problema (#404).
 * `hojeISO`: "AAAA-MM-DD".
 */
export function tabelasVencidasSemVigente(tabelas, hojeISO) {
  const norm = c => String(c || "").replace(/^0+/, "");
  const dia = d => (d || "").slice(0, 10);
  const vigenteHoje = t => (dia(t.vigIni) || "0000-01-01") <= hojeISO && hojeISO <= (dia(t.vigFim) || "9999-12-31");
  const comVigente = new Set((tabelas || []).filter(vigenteHoje).map(t => norm(t.codTransp)));
  return (tabelas || []).filter(t => vigStatus(t.vigFim).cod === "expirada" && !comVigente.has(norm(t.codTransp)));
}

/**
 * OTIF (%) das views mensais de KPI a partir de `mesIni` ("AAAA-MM"): no prazo /
 * (no prazo + em atraso), a mesma conta do Dashboard (#410). null sem dados.
 */
export function otifDasViews(gerais, mesIni) {
  const linhas = (gerais || []).filter(r => r.mes >= mesIni);
  const noPrazo = linhas.reduce((a, r) => a + (+r.no_prazo || 0), 0);
  const emAtraso = linhas.reduce((a, r) => a + (+r.em_atraso || 0), 0);
  return noPrazo + emAtraso > 0 ? Math.round(noPrazo / (noPrazo + emAtraso) * 100) : null;
}

/**
 * % de NFs incorretas das views mensais de KPI a partir de `mesIni` ("AAAA-MM"):
 * só status "incorreto", como o Dashboard e a tela Notas (#413). null sem dados.
 */
export function incorretasDasViews(gerais, mesIni) {
  const linhas = (gerais || []).filter(r => r.mes >= mesIni);
  const total = linhas.reduce((a, r) => a + (+r.total_nfs || 0), 0);
  const incorretas = linhas.reduce((a, r) => a + (+r.incorretas || 0), 0);
  return total > 0 ? Math.round(incorretas / total * 100) : null;
}

export function useAlertas({ notas, tabelas, ocorrencias, clientesMap, municipiosMap, auditorias, rastreioSsw, usuario, kpis }) {
  const alertas = useMemo(() => {
    const lista = [];
    const hoje  = new Date(); hoje.setHours(0,0,0,0);
    const id    = () => Math.random().toString(36).slice(2,9);

    // ── 1. OTIF crítico (<75%) ─────────────────────────────────
    // Mesmas views e mesmo escopo padrão (3 meses) do Dashboard (#410). Sem as
    // views, calcula pela lista de notas carregada.
    let otifPct = otifDasViews(kpis?.gerais, inicioJanela(hojeSP()).slice(0, 7));
    if (otifPct == null) {
      const entregues = (notas||[]).filter(n => n.dataEntregaReal && n.statusNF !== "devolvida");
      let noPrazo = 0;
      entregues.forEach(n => {
        try {
          const val   = validarNFCache(n, clientesMap, municipiosMap);
          const prazo = val?.prazo || 1;
          const base  = n.dataISO || brToISO(n.dataEmbarque) || brToISO(n.dataEmissao);
          if (!base) return;
          const prev = addDiasUteis(base, prazo);
          if (!prev || prev === "—") return;
          const [d,m,a]   = prev.split("/").map(Number);
          const [de,me,ae]= n.dataEntregaReal.split("/").map(Number);
          if (new Date(ae,me-1,de) <= new Date(a,m-1,d)) noPrazo++;
        } catch {}
      });
      otifPct = entregues.length > 0 ? Math.round((noPrazo / entregues.length) * 100) : null;
    }
    if (otifPct != null && otifPct < 75) {
      lista.push({
        id: "otif-critico", tipo:"critico", icone:"alerta", modulo:"dashboard",
        titulo:"OTIF Crítico",
        msg:`OTIF em ${otifPct}% — abaixo de 75%. Revise prazos e transportadoras.`,
        valor: otifPct + "%",
      });
    } else if (otifPct != null && otifPct < 85) {
      lista.push({
        id: "otif-atencao", tipo:"atencao", icone:"alerta", modulo:"dashboard",
        titulo:"OTIF Abaixo da Meta",
        msg:`OTIF em ${otifPct}% — meta é 95%. Monitore transportadoras.`,
        valor: otifPct + "%",
      });
    }

    // ── 2. Tabelas de frete vencidas ou vencendo ───────────────
    // Vencida só alerta se a transportadora ficou sem tabela vigente (#404).
    tabelasVencidasSemVigente(tabelas, hoje.toLocaleDateString("sv-SE")).forEach(t => {
      lista.push({
        id: "tab-venc-"+t.cod, tipo:"critico", icone:"calendario", modulo:"tabelas",
        titulo:"Tabela de Frete Vencida",
        msg:`${t.transp} — ${t.cod} está vencida desde ${t.vigFim} e a transportadora não tem tabela vigente. Atualize imediatamente.`,
        valor: t.cod, em: t.vigFim,
      });
    });
    (tabelas||[]).forEach(t => {
      const s = vigStatus(t.vigFim);
      if (s.cod === "a_vencer") {
        lista.push({
          id: "tab-breve-"+t.cod, tipo:"atencao", icone:"calendario", modulo:"tabelas",
          titulo:"Tabela Vence em Breve",
          msg:`${t.transp} — ${t.cod} vence em ${t.vigFim}. Providencie a renovação.`,
          valor: t.cod, em: t.vigFim,
        });
      }
    });

    // ── 3. Ocorrências críticas sem resposta há +7 dias ────────
    const ocUrg = (ocorrencias||[]).filter(o => {
      if (o.status === "finalizado" || !o.dataAberturaISO) return false;
      return (hoje - new Date(o.dataAberturaISO)) / 86400000 > 7;
    });
    if (ocUrg.length > 0) {
      lista.push({
        id: "oc-urgentes", tipo:"atencao", icone:"alerta", modulo:"ocorrencias",
        titulo:"Ocorrências Sem Resposta",
        msg:`${ocUrg.length} ocorrência(s) abertas há mais de 7 dias sem resolução.`,
        valor: ocUrg.length,
        // A mais antiga: é ela que diz há quanto tempo o problema existe.
        em: ocUrg.map(o => o.dataAberturaISO).sort()[0],
      });
    }

    // ── 4. Divergências de conferência ─────────────────────────
    //
    // Vinha do localStorage: o alerta só existia no navegador de quem tinha
    // rodado a conferência (#141). Agora sai do banco, e o administrador
    // recebe também o que a equipe rodou — marcado com `deOutro`, para o
    // painel separar em duas listas. Sem essa separação, um dia de equipe
    // conferindo enterra os alertas do próprio usuário.
    const admin = usuario?.cargo === "admin";
    (auditorias || [])
      .filter(a => a.divergem > 0 && (admin || a.criado_por === usuario?.id))
      .slice(0, 12)   // o painel é uma lista de leitura, não um relatório
      .forEach(a => {
        const quem = a.criado_por === usuario?.id ? "" : ` · ${a.criado_por_nome || "outro usuário"}`;
        const onde = a.tipo === "foto" ? "auditoriaFoto" : "auditoria";
        const nome = a.resumo?.transportadora?.nome || a.resumo?.transp || "transportadora não identificada";
        lista.push({
          id: "audit-div-" + a.id, tipo:"atencao", icone:"lupa", modulo: onde,
          titulo: a.tipo === "foto" ? "Divergências na Auditoria por Foto" : "Divergências de Auditoria",
          msg:`${a.divergem} divergência(s) na conferência de ${nome}${quem}.`,
          valor: a.divergem, em: a.criado_em,
          deOutro: !!usuario?.id && a.criado_por !== usuario.id,
        });
      });

    // ── 5. NFs incorretas acima de 5% ─────────────────────────
    // Mesmas views e escopo (3 meses) do Dashboard (#413). Sem as views, valida
    // as 2.000 primeiras notas da lista.
    let pctIncorr = incorretasDasViews(kpis?.gerais, inicioJanela(hojeSP()).slice(0, 7));
    if (pctIncorr == null) {
      let incorr = 0;
      (notas||[]).slice(0, 2000).forEach(n => {
        try {
          const v = validarNFCache(n, clientesMap, municipiosMap);
          if (v.status === "incorreto" || v.status === "verificar") incorr++;
        } catch {}
      });
      pctIncorr = notas?.length > 0 ? Math.round((incorr / Math.min(2000, notas.length)) * 100) : 0;
    }
    if (pctIncorr >= 10) {
      lista.push({
        id: "nf-incorr", tipo:"critico", icone:"caminhao", modulo:"notas",
        titulo:"Alto índice de NFs Incorretas",
        msg:`${pctIncorr}% das NFs com transportadora incorreta. Revise o cadastro de municípios.`,
        valor: pctIncorr + "%",
      });
    } else if (pctIncorr >= 5) {
      lista.push({
        id: "nf-incorr-at", tipo:"atencao", icone:"caminhao", modulo:"notas",
        titulo:"NFs com Transportadora Incorreta",
        msg:`${pctIncorr}% das NFs com transportadora incorreta.`,
        valor: pctIncorr + "%",
      });
    }

    // ── 6. Rastreio automático (#326, #331) ────────────────────
    // O robô marca a nota; aqui só se conta. Detalhe por nota na tela Notas.
    const ssw = contarAlertasSsw(rastreioSsw);
    [["devolucao", "critico", "desfazer"], ["parcial", "critico", "caixa"], ["atraso", "critico", "historico"], ["sem_cte", "atencao", "documento"], ["sem_info", "atencao", "sinal"]]
      .forEach(([cod, tipo, icone]) => {
        if (!ssw[cod]) return;
        lista.push({
          id: "ssw-" + cod, tipo, icone, modulo: "notas",
          titulo: ALERTAS_SSW[cod],
          msg: `${ssw[cod]} nota(s) pendente(s) com "${ALERTAS_SSW[cod]}" no rastreio automático.`,
          valor: ssw[cod],
        });
      });

    // Ordenar: críticos primeiro
    return lista.sort((a,b) => a.tipo === "critico" && b.tipo !== "critico" ? -1 : 1);
  }, [notas, tabelas, ocorrencias, clientesMap, municipiosMap, auditorias, rastreioSsw, usuario, kpis]);

  return alertas;
}
